import { PrincipalIdSchema } from "@core/domains/identity";
import { identity } from "@core/foundation/function";
import { createModel, createModelSchemas } from "@core/foundation/model";
import { z } from "@core/zod";

export const GithubComment = createModel({
  create: (item, schema) => {
    return { ...schema.decode(item) };
  },
  schemas: createModelSchemas({
    base: z.object(
      {
        author: z.string(),
        authorType: z.string(),
        body: z.string(),
        diffHunk: z.string().nullable(),
        githubCommentCreatedAt: z.iso.datetime(),
        githubCommentDeleted: z.boolean(),
        githubCommentUpdatedAt: z.iso.datetime(),
        githubRepoId: z.int().nonnegative(),
        id: z.int().nonnegative(),
        lastSyncedAt: z.iso.datetime(),
        path: z.string(),
        prNumber: z.int().nonnegative(),
        principalId: PrincipalIdSchema,
        threadId: z.int().nonnegative(),
        url: z.url(),
      },
      {
        author: "The GitHub login of the comment's author.",
        authorType: "The type of the author's GitHub account.",
        body: "The comment's Markdown body.",
        diffHunk: "The diff hunk GitHub showed the reviewer, or null.",
        githubCommentCreatedAt: "When GitHub records the comment as created.",
        githubCommentDeleted: "Whether GitHub no longer holds the comment.",
        githubCommentUpdatedAt: "When GitHub records the comment as updated.",
        githubRepoId: "The GitHub id of the repo the comment belongs to.",
        id: "The comment's GitHub id.",
        lastSyncedAt: "When the record was last brought in line with GitHub.",
        path: "The path of the file the comment is on.",
        prNumber: "The number of the pull request the comment is on.",
        principalId: "The id of the principal the record belongs to.",
        threadId: "The id of the review thread the comment belongs to.",
        url: "The comment's URL on GitHub.",
      },
    ),
    defaults: { githubCommentDeleted: false },
    derive: {
      create: identity,
      recovery: identity,
      update: schema =>
        schema
          .pick({
            body: true,
            githubCommentDeleted: true,
            githubCommentUpdatedAt: true,
            lastSyncedAt: true,
          })
          .partial(),
    },
  }),
  update: (item, update, schema) => {
    return { ...item, ...schema.decode(update) };
  },
});

export interface GithubComment extends z.infer<typeof GithubComment.schema> {}
